import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { FeedbackService } from '../../../../core/feedback.service';
import { switchMap, take, takeUntil } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { sendToGoogleAnalytics } from '../../../../core/utilities';
import { ApiService } from '../../../../core/api-service';
import { Store } from '@ngrx/store';
import { GbpLocationGroup } from '../../../../models/locations-models/gbp-location-group';
import * as locationListSelectors from '../../location-list/state/location-list-selectors';
import * as ccListSelectors from '../state/cc-list-selectors';
import { selectAccountID } from '../../../../store/global/core-selectors';
import { CCActionBarActions } from '../state/cc-list-actions';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatFormField } from '@angular/material/form-field';

/**
 * Validator that returns an error if the input is null.
 */
export const notNullValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  return !control.value ? { isNull: true } : null;
};

@Component({
    selector: 'app-cc-gmb-location-grp',
    templateUrl: './cc-gmb-location-grp.component.html',
    styleUrls: ['./cc-gmb-location-grp.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatFormField,
        MatSelect,
        FormsModule,
        ReactiveFormsModule,
        MatOption,
        MatIconButton,
        MatMenuTrigger,
        NgClass,
        MatIcon,
        MatMenu,
    ],
})
export class CcGmbLocationGrpComponent implements OnInit, OnDestroy {

  selectedGrp: FormControl = new FormControl<GbpLocationGroup|null>(null, [notNullValidator]);
  focusedLocation$ = this.store.select(locationListSelectors.selectFocusedLocation);
  infoMenuOpened = false;

  private componentDestroyed = new Subject<void>();

  constructor(private store: Store,
              private apiService: ApiService,
              private feedbackService: FeedbackService,
  ) {
  }


  ngOnInit(): void {
    this.store.select(ccListSelectors.selectCcLocationGroup)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((ccLocationGroup) => {
          this.selectedGrp.setValue(ccLocationGroup, { emitEvent: false });
          this.selectedGrp.markAsTouched();
          this.selectedGrp.updateValueAndValidity();
        });

    this.focusedLocation$
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((v) => !!v ?
            this.selectedGrp.enable({ emitEvent: false }) :
            this.selectedGrp.disable({ emitEvent: false }),
        );

    this.selectedGrp.valueChanges
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(group => {
          this.onClaimingLocationGroupChange(group);
        });
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next();
  }

  /**
   * Attempts to change the currently selected claiming location group
   */
  onClaimingLocationGroupChange(group: { accountName: string, gmbAccountId: string }) {
    if (!group) {
      return;
    }

    sendToGoogleAnalytics('C&C actions',
        'Set claiming location group');

    this.store.select(selectAccountID)
        .pipe(
            take(1),
            switchMap((accountID) => this.apiService
                .setClaimingLocationGroupId(accountID, group?.gmbAccountId)),
            takeUntil(this.componentDestroyed),
        )
        .subscribe({
          next: () => {
            // TODO: set claimingLocationGroupId to ngrx store so we don't have to call API
            //  in setDefaultClaimingLocationGroup effect
            this.store.dispatch(CCActionBarActions.updateCCLocationGroup({ ccLocationGroup: group }));
          }, error: (err: HttpErrorResponse) => {
            // set new form value, but don't trigger form value change detection
            this.selectedGrp.setValue(null, { emitEvent: false });
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Setting location group for Claim & Clean failed.',
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
              },
            }, err?.status);
          },
        });
  }

  /**
   * Event handler for opening info menu
   */
  onInfoMenuOpened(): void {
    this.infoMenuOpened = true;
  }

  /**
   * Event handler for closing info menu
   */
  onInfoMenuClosed(): void {
    this.infoMenuOpened = false;
  }

  getGbpGroups(): GbpLocationGroup[] {
    let gbpGroups: GbpLocationGroup[];
    this.store.select(locationListSelectors.selectGbpLocationGroups)
        .pipe(take(1))
        .subscribe((v) => gbpGroups = v);
    return gbpGroups;
  }

}
